import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Heart, Lock } from "lucide-react";

const SharedToolkit = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "This share link is no longer available."));
  }, [token]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="share-error">
        <Lock className="mx-auto text-neutral-500 mb-4" size={40} />
        <h1 className="font-display text-3xl mb-3">Link closed</h1>
        <p className="text-neutral-400 mb-8">{err}</p>
        <Link to="/" className="btn-primary px-5 py-2.5">Return home</Link>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;

  const { sender, note, toolkit, reflections } = data;

  return (
    <div className="fade-in max-w-3xl mx-auto px-6 py-16" data-testid="share-view">
      <div className="mb-10 rounded-2xl border border-teal-500/25 bg-teal-500/5 p-6 flex items-start gap-4">
        <Heart className="text-teal-300 flex-shrink-0 mt-1" size={22} />
        <div>
          <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-2">Shared with you</div>
          <p className="text-neutral-100 leading-relaxed">
            <span className="text-teal-200 font-medium">{sender}</span> shared their reflections from a
            Why We Recover toolkit. This is a one-time view — please treat it with care.
          </p>
          {note && (
            <div className="mt-4 border-l-2 border-teal-500/50 pl-4 text-neutral-200 italic">
              "{note}"
            </div>
          )}
        </div>
      </div>

      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Toolkit</div>
      <h1 className="font-display text-4xl md:text-5xl mb-3">{toolkit.title}</h1>
      {toolkit.subtitle && (
        <p className="font-display text-lg text-teal-200/80 mb-6">{toolkit.subtitle}</p>
      )}
      <p className="text-neutral-400 leading-relaxed mb-8 max-w-2xl">{toolkit.description}</p>

      {toolkit.disclaimer && (
        <div className="mb-10 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100/90 leading-relaxed">
          {toolkit.disclaimer}
        </div>
      )}

      <div className="space-y-4">
        {toolkit.sections.map((s, i) => {
          const rtext = reflections?.[String(i)];
          return (
            <div key={i} className="card-surface p-8">
              <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-2">Section {i + 1}</div>
              <h2 className="font-display text-2xl mb-4">{s.title}</h2>
              <p className="text-neutral-200 leading-relaxed whitespace-pre-line">{s.body}</p>
              {rtext && (
                <div className="mt-6 rounded-xl border border-teal-500/25 bg-teal-500/5 p-4">
                  <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-2">{sender}'s reflection</div>
                  <p className="text-neutral-100 leading-relaxed whitespace-pre-line">{rtext}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-16 text-center">
        <p className="text-sm text-neutral-500 mb-4">
          This link has now closed. If you'd like your own space, Why We Recover is a pre-therapy toolkit anyone can use.
        </p>
        <Link to="/" className="btn-ghost px-5 py-2">Learn more</Link>
      </div>
    </div>
  );
};

export default SharedToolkit;
